import { useState } from "react";
import type { PatientInfo } from "@hearing/shared";
import { Layout } from "../components/Layout.js";
import { Button } from "../components/Button.js";
import { useScreener } from "../state.js";
import { useFlow } from "./useFlow.js";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function Details() {
  const { patient, setPatient, markCompleted, config } = useScreener();
  const { go } = useFlow();
  const [name, setName] = useState(patient.name ?? "");
  const [email, setEmail] = useState(patient.email ?? "");
  const [phone, setPhone] = useState(patient.phone ?? "");
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  const nameOk = name.trim().length > 0;
  const emailOk = EMAIL_RE.test(email.trim());
  const phoneOk = phone.trim().length >= 6;
  const valid = nameOk && emailOk && phoneOk;

  async function submit() {
    setTouched(true);
    if (!valid || saving) return;
    const details: PatientInfo = {
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
    };
    setPatient(details);
    setSaving(true);
    await markCompleted();
    go("results");
  }

  return (
    <Layout step="details">
      <h2 className="center">Your details</h2>
      <p className="muted center screen-copy">
        Your hearing check is complete. Enter your details so the{" "}
        {config?.brand?.teamName ?? "Hearing Expert audiology team"} can share
        your result and follow up if needed.
      </p>

      <div className="form-grid">
        <div className="field">
          <label htmlFor="name">Full name</label>
          <input
            id="name"
            className="text-input"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe"
            aria-invalid={touched && !nameOk}
            aria-describedby={touched && !nameOk ? "name-error" : undefined}
          />
          {touched && !nameOk ? (
            <div className="field-error" id="name-error">Please enter your name.</div>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="email">Email address</label>
          <input
            id="email"
            className="text-input"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jane@example.com"
            aria-invalid={touched && !emailOk}
            aria-describedby={touched && !emailOk ? "email-error" : undefined}
          />
          {touched && !emailOk ? (
            <div className="field-error" id="email-error">Please enter a valid email address.</div>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="phone">Phone number</label>
          <input
            id="phone"
            className="text-input"
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+44 7700 900000"
            aria-invalid={touched && !phoneOk}
            aria-describedby={touched && !phoneOk ? "phone-error" : undefined}
          />
          {touched && !phoneOk ? (
            <div className="field-error" id="phone-error">Please enter a contact phone number.</div>
          ) : null}
        </div>
      </div>

      <div className="actions">
        <Button variant="secondary" onClick={() => go("questions")} disabled={saving}>
          Back
        </Button>
        <Button onClick={submit} disabled={saving}>
          {saving ? "Preparing results…" : "See my results"}
        </Button>
      </div>

      <p className="center muted privacy-copy">
        Your details and results are shared only with the{" "}
        {config?.brand?.teamName ?? "Hearing Expert audiology team"} to follow up
        on your screening.
      </p>
    </Layout>
  );
}
